import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Textarea } from './Textarea';
import type { SubscriptionTier } from '@maker-toolchest/shared';

interface TierDowngradeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  currentTier: SubscriptionTier;
  targetTier: SubscriptionTier;
  effectiveDate: Date;
  loading?: boolean;
}

const TIER_NAMES: Record<SubscriptionTier, string> = {
  TINKERER: 'Tinkerer',
  HOBBYIST: 'Hobbyist',
  BUSINESS: 'Business',
};

export function TierDowngradeModal({
  open,
  onClose,
  onConfirm,
  currentTier,
  targetTier,
  effectiveDate,
  loading = false,
}: TierDowngradeModalProps) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    onConfirm(reason || undefined);
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const formattedDate = effectiveDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const isCancel = targetTier === 'TINKERER';

  return (
    <Modal open={open} onClose={handleClose} size="md">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning/10">
            <AlertTriangle className="h-6 w-6 text-warning" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">
              {isCancel ? 'Cancel Subscription' : `Downgrade to ${TIER_NAMES[targetTier]}`}
            </h2>
            <p className="mt-1 text-sm text-foreground-muted">
              {isCancel
                ? 'Your subscription will be cancelled and you will be moved to the free tier.'
                : `Your plan will be downgraded to ${TIER_NAMES[targetTier]}.`}
            </p>
          </div>
        </div>

        {/* Info Notice */}
        <div className="rounded-lg border border-primary bg-primary/5 p-4">
          <p className="text-sm text-foreground">
            <strong>Your access will continue until {formattedDate}.</strong>
          </p>
          <p className="mt-2 text-sm text-foreground-muted">
            You'll retain full access to your {TIER_NAMES[currentTier]} plan features until this date.
            After that, your plan will change to {TIER_NAMES[targetTier]}.
          </p>
        </div>

        {/* Optional Reason */}
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">
            Help us improve (optional)
          </label>
          <Textarea
            placeholder="Let us know why you're downgrading..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          <p className="mt-1 text-xs text-foreground-muted">
            Your feedback helps us make Maker Toolchest better for everyone.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
          <Button variant="ghost" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleConfirm}
            loading={loading}
          >
            {isCancel ? 'Confirm Cancellation' : 'Confirm Downgrade'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
